var __importDefault = (this && this.__importDefault) || function (mod) {
  return (mod && mod.__esModule) ? mod : { "default": mod };
};
/**
 * MiniMonth Component
 *
 * One of the two compact side calendars of the monthly Resource Dashboard view.
 * Legacy renders these as two extra `<richcc-datepicker>` instances configured
 * with `dtPickerOptionsLight` (calendar.controller.js:1778-1789) bound to the
 * SAME event array as the main month (calendardashboardpartial.html:196-203),
 * which is why this takes the already-fetched `events` rather than issuing its
 * own request.
 *
 * The month shown is fully controlled by `activeDate` - it must be derived from
 * the main calendar's active date at render time, never from a literal (legacy
 * `self.today()` hard-codes 2016 dates at calendar.controller.js:2066-2067,
 * which is exactly the bug not to reproduce).
 */
import React, { memo, useMemo } from 'react';
import { useCalendarGrid } from '../../hooks/useCalendarGrid';
import { useEventProcessing } from '../../hooks/useEventProcessing';
import { DayPicker } from '../DayPicker';
import { format } from 'date-fns';
export const MiniMonth = memo(({ activeDate, events, config, uidPrefix, onMonthSelect, }) => {
    // Side months never show week numbers or navigation (legacy dtPickerOptionsLight).
    const miniConfig = useMemo(() => (Object.assign(Object.assign({}, config), { datepickerMode: 'day', showWeeks: false, navigation: false, hideCalNav: true, preventCalNav: true, showMarkerForMoreEvents: false, showAddEventOnHover: false })), [config]);
    const grid = useCalendarGrid({
        activeDate,
        selectedDate: null,
        config: miniConfig,
        uidPrefix,
        viewMode: 'day',
    });
    const visibleDays = useMemo(() => {
        const days = [];
        grid.calendarRows.forEach(row => row.days.forEach(day => days.push(day.date)));
        return days;
    }, [grid.calendarRows]);
    const eventProcessing = useEventProcessing({
        events,
        visibleDays,
        startingDay: miniConfig.startingDay,
        expandedMode: false,
    });
    const title = format(activeDate, config.formatDayTitle || 'MMMM yyyy');
    return (React.createElement(DayPicker, { rows: grid.calendarRows, dayHeaders: grid.dayHeaders, eventMap: eventProcessing.eventMap, config: miniConfig, title: title, idPrefix: uidPrefix, compact: true, 
        // Anchoring focus on the month's own start keeps exactly one cell in the
        // tab order, so the grid is reachable by keyboard rather than being a
        // role="grid" that nothing can enter.
        focusedDate: activeDate, onTitleClick: onMonthSelect ? () => onMonthSelect(activeDate.getMonth(), activeDate.getFullYear()) : undefined, titleActionLabel: `Show ${title} in the main calendar` }));
});
MiniMonth.displayName = 'MiniMonth';
export default MiniMonth;
//# sourceMappingURL=MiniMonth.js.map